import React from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate
} from 'react-router-dom';
import ProfilePage from '../../pages/ProfilePage/ProfilePage';
import LoginPage from '../../pages/LoginPage/LoginPage';
import PasswordPage from '../../pages/PasswordPage/PasswordPage';
import ConversationsPage from '../../pages/ConversationsPage/ConversationsPage';
import ForgotPasswordPage from '../../pages/ForgotPasswordPage/ForgotPasswordPage';
import UserManagementPage from '../../pages/UserManagementPage/UserManagementPage';
import BotBuilderPage from '../../pages/BotBuilderPage/BotBuilderPage';
import TwoFaAuthenticationPage from '../../pages/TwoFaAuthenticationPage/TwoFaAuthenticationPage';
import RequireFullAuth from '../RequireFullAuth/RequireFullAuth';
import RequireNoAuth from '../RequireNoAuth/RequireNoAuth';
import withPageWrapper from '../HOC/withPageWrapper/withPageWrapper';
import EmbeddingInstructionsPage from '../../pages/EmbeddingInstructionsPage/EmbeddingInstructionsPage';
import BotTestPage from '../../pages/BotTestPage/BotTestPage';
import AnalyticsPage from '../../pages/AnalyticsPage/AnalyticsPage';
import FullSizeChatbotPage from '../FullSizeChatbotPage/FullSizeChatbotPage';
import DateRangeProvider from '../../context/DateRangeContext/DateRangeProvider';
import ConversationSidebar from '../../pages/ConversationSidebar/ConversationSidebar';
import TrainingPage from '../../pages/TrainingPage/TrainingPage';
import ChatbotSettingsPage from '../../pages/ChatbotSettingsPage/ChatbotSettingsPage';
import UserPage from '../../pages/UserPage/UserPage';
import IssuesPage from '../../pages/IssuesPage/IssuesPage';
import OrganisationSettingsPage from '../../pages/OrganisationSettingsPage/OrganisationSettingsPage';

//  Requiring Auth Pages
const WrappedProfilePage = withPageWrapper(ProfilePage);
const WrappedConversationsPage = withPageWrapper(ConversationsPage);
const WrappedConversationSidebar = withPageWrapper(ConversationSidebar);
const WrappedUserManagementPage = withPageWrapper(UserManagementPage);
const WrappedBotBuilderPage = withPageWrapper(BotBuilderPage);
const WrappedTrainingPage = withPageWrapper(TrainingPage);
const WrappedChatbotSettingsPage = withPageWrapper(ChatbotSettingsPage);
const WrappedEmbeddingInstructionsPage = withPageWrapper(
  EmbeddingInstructionsPage
);
const WrappedUserPage = withPageWrapper(UserPage);
const WrappedIssuesPage = withPageWrapper(IssuesPage);
const WrappedOrganisationSettingsPage = withPageWrapper(
  OrganisationSettingsPage
);

//  Requiring No Auth Pages
const WrappedLoginPage = withPageWrapper(LoginPage);
const WrappedTwoFaAuthenticationPage = withPageWrapper(TwoFaAuthenticationPage);
const WrappedForgotPasswordPage = withPageWrapper(ForgotPasswordPage);
const WrappedPasswordPage = withPageWrapper(PasswordPage);
const WrappedAnalyticsPage = withPageWrapper(AnalyticsPage);

function AppRoutes(): React.ReactElement {
  return (
    <Router>
      <DateRangeProvider>
        <Routes>
          <Route index element={<Navigate to='/login' replace />} />
          <Route
            path='/public/chatbot-view/:chatbotUUID'
            element={<BotTestPage />}
          />
          <Route
            path='/public/chatbots/:chatbotUUID'
            element={<FullSizeChatbotPage />}
          />
          {/* Protected Pages */}
          <Route element={<RequireFullAuth />}>
            <Route path='profile' element={<WrappedProfilePage />} />
            <Route
              path='embed-instructions'
              element={<WrappedEmbeddingInstructionsPage />}
            />
            <Route path='chats' element={<WrappedConversationsPage />} />
            <Route
              path='chats/:chatId'
              element={<WrappedConversationSidebar />}
            />
            <Route path='users' element={<WrappedUserManagementPage />} />
            <Route path='users/:id' element={<WrappedUserPage />} />
            <Route
              path='org-settings'
              element={<WrappedOrganisationSettingsPage />}
            />
            <Route path='issues' element={<WrappedIssuesPage />} />
            <Route
              path='bot-builder/:chatbotId/:intentId'
              element={<WrappedBotBuilderPage />}
            />
            <Route path='training' element={<WrappedTrainingPage />} />
            <Route path='settings' element={<WrappedChatbotSettingsPage />} />
            <Route path='analytics' element={<WrappedAnalyticsPage />} />
          </Route>
          {/* Auth Pages */}
          <Route element={<RequireNoAuth />}>
            <Route path='login' element={<WrappedLoginPage />} />
            <Route path='2fa' element={<WrappedTwoFaAuthenticationPage />} />
            <Route
              path='forgot-password'
              element={<WrappedForgotPasswordPage />}
            />
            <Route path='set-password' element={<WrappedPasswordPage />} />
          </Route>
          <Route path='*' element={<Navigate to='/login' replace />} />
        </Routes>
      </DateRangeProvider>
    </Router>
  );
}

export default AppRoutes;
